<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-50" @close="open = false">
      <TransitionChild
        as="template"
        enter="ease-in-out duration-500"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in-out duration-500"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-25 dark:bg-neutral-500 dark:bg-opacity-25 transition-opacity" />
      </TransitionChild>
      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel class="pointer-events-auto w-screen max-w-2xl">
                <AppConfirmAction v-model="show_cofirm" :title="confirm_title" :message="confirm_message" @confirmed="actionConfirmed()" />
                <div class="flex h-full flex-col overflow-auto bg-gray-50 dark:bg-neutral-950 py-6 shadow-xl">
                  <!-- Header -->
                  <div class="px-4 sm:px-6">
                    <div class="flex items-start justify-between">
                      <DialogTitle class="text-base font-semibold leading-6 text-gray-900">
                        <h3 class="text-xl font-bold text-gray-900 sm:text-2xl dark:text-white">
                          {{ props.account.parties.first_name + " " + props.account.parties.last_name }}
                        </h3>
                        <div class="text-gray-400 dark:text-neutral-400 truncate flex items-center gap-1">
                          {{ props.account.parties.contact_email }}
                          <AppCopy :text="props.account.parties.contact_email" class="mb-1" />
                        </div>
                        <div class="text-gray-400 dark:text-neutral-400 truncate flex items-center gap-1 text-xs">
                          {{ props.account.parties.tapi_party_id }}
                          <AppCopy :text="props.account.parties?.tapi_party_id ?? ''" class="mb-0.5 opacity-70" />
                        </div>
                      </DialogTitle>
                      <div class="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          class="relative rounded-md text-gray-400 hover:text-gray-500 dark:text-neutral-400 dark:hover:text-neutral-300 focus:outline-none"
                          @click="open = false"
                        >
                          <span class="absolute -inset-2.5" />
                          <span class="sr-only">Close panel</span>
                          <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <!-- Header -->

                  <!-- Content -->
                  <div class="relative mt-6 flex-1 flex flex-col justify-between">
                    <div class="sm:mx-6 mx-4 flex flex-col gap-10">
                      <div class="flex flex-col gap-5">
                        <!-- Update Accreditation Status Super Admin Only -->
                        <div
                          v-if="userStore.user.profiles_protected.role == 'super_admin'"
                          class="flex flex-wrap gap-5 p-5 rounded-lg construction-bg bg-transparent dark:bg-neutral-700 shadow"
                        >
                          <div class="flex-1 flex justify-between gap-5 flex-wrap items-end">
                            <div class="w-full">
                              <SafeTextarea id="ainotes" v-model="notes" :rows="3" label="Accreditation Request Notes" class="w-full" />
                            </div>
                            <div class="flex gap-5 items-end">
                              <AppSelect
                                v-model="accreditationStatus"
                                label="Accreditation Status"
                                info="Changing this status will update the accounts accreditation request status in TAPI. If the account has not yet requested accreditation, this will trigger a request and immediately approve it."
                                :items="['Accredited', 'Under Review', 'Action Required', 'Not Accredited']"
                                class="w-40"
                              />
                              <AppSelect
                                v-model="reason_for_accreditation"
                                label="Accreditation Reason"
                                :items="['Income', 'Assets', 'All Parties Accredited', 'Registered Person', 'Professional Letter', 'Pending']"
                                class="w-52"
                              />
                              <div class="button_yellow h-fit" @click="updateAccreditationStatus(props.account)">
                                Save Changes
                                <SpinnerButton v-if="updatingAccreditedStatus" />
                                <CloudArrowUpIcon v-else class="w-5 h-5" />
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- Accredited Investors Docs -->
                        <AccreditationWidget
                          v-if="!partnerStore.partner.disable_accreditation"
                          :account="props.account"
                          :user_id="props.user_id"
                          @success="emit('silent_success')"
                        />
                        <!-- KYC/AML Docs -->
                        <KycAmlWidget
                          v-if="!partnerStore.partner.partners_protected.kyc_disabled"
                          :party="props.account.parties"
                          :user_id="props.user_id"
                          @success="emit('silent_success')"
                        />
                        <!-- Entity Documents -->
                        <EntityDocuments v-if="props.account.type == 'entity'" :party="props.account.parties" :user_id="props.user_id" />
                      </div>

                      <!-- Secondary Party (Optional) -->
                      <div v-if="props.account.secondary_party" class="flex flex-col">
                        <div class="flex items-center justify-center mt-1 w-full">
                          <div class="w-1/2 border-t border-gray-300"></div>
                          <span class="px-3 text-gray-600 whitespace-nowrap">Secondary Party</span>
                          <div class="w-1/2 border-t border-gray-300"></div>
                        </div>
                        <div class="flex flex-col gap-5 mt-5">
                          <div class="text-base font-semibold leading-6 text-gray-900">
                            <h3 class="text-xl font-bold text-gray-900 sm:text-2xl dark:text-white">
                              {{ props.account.secondary_party.first_name + " " + props.account.secondary_party.last_name }}
                            </h3>
                            <div class="text-gray-400 dark:text-neutral-400 truncate flex items-center gap-1">
                              {{ props.account.secondary_party.contact_email }}
                              <AppCopy :text="props.account.secondary_party.contact_email" class="mb-1" />
                            </div>
                            <div class="text-gray-400 dark:text-neutral-400 truncate flex items-center gap-1 text-xs">
                              {{ props.account.parties.tapi_party_id }}
                              <AppCopy :text="props.account.secondary_party.tapi_party_id" class="mb-0.5 opacity-70" />
                            </div>
                          </div>
                          <!-- KYC/AML Docs -->
                          <KycAmlWidget
                            v-if="!partnerStore.partner.partners_protected.kyc_disabled"
                            :party="props.account.secondary_party"
                            :user_id="props.user_id"
                            @success="emit('silent_success')"
                          />
                          <!-- Entity Documents -->
                          <EntityDocuments v-if="props.account.type == 'entity'" :party="props.account.secondary_party" :user_id="props.user_id" />
                        </div>
                      </div>
                    </div>

                    <div class="sm:mx-6 mx-4 mt-8">
                      <div class="flex justify-end gap-5 flex-wrap">
                        <AppButtonSecondary
                          size="lg"
                          text="Request Opera Manual Review"
                          :icon="MegaphoneIcon"
                          :loading="requestingReview"
                          @click="confirmAction('request_review')"
                        />

                        <AppButtonSecondary
                          size="lg"
                          text="Run KYC & AML"
                          :icon="ArrowPathIcon"
                          :loading="runningKycAml"
                          @click="confirmAction('run_kyc_aml')"
                        />

                        <AppButtonSecondary
                          size="lg"
                          text="Run AML"
                          :icon="ShieldCheckIcon"
                          :loading="runningAml"
                          @click="confirmAction('run_aml')"
                        />
                      </div>
                    </div>
                  </div>
                  <!-- Content -->
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
// Essentials
import { ref, watch, onMounted, computed } from "vue";
import { useUserStore } from "@/stores/user";
import { usePartnerStore } from "@/stores/partner";
import { usePartiesStore } from "@/stores/parties";
import { useEmailsStore } from "@/stores/emails";
import { useAccountsStore } from "@/stores/accounts";

// Components
import AppSelect from "@/components/applicationinputs/AppSelect.vue";
import EntityDocuments from "@/components/investors/EntityDocuments.vue";
import KycAmlWidget from "@/components/investors/KycAmlWidget.vue";
import AccreditationWidget from "@/components/investors/AccreditationWidget.vue";
import SpinnerButton from "@/components/loading/SpinnerButton.vue";
import AppButtonSecondary from "@/components/applicationui/AppButtonSecondary.vue";
import AppConfirmAction from "@/components/applicationui/AppConfirmAction.vue";
import AppCopy from "@/components/applicationui/AppCopy.vue";
import SafeTextarea from "@/components/applicationinputs/safeTextarea.vue";

// Libraries
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { XMarkIcon, ArrowPathIcon, ShieldCheckIcon, MegaphoneIcon } from "@heroicons/vue/24/outline";
import { CloudArrowUpIcon } from "@heroicons/vue/24/solid";

// Props
const props = defineProps({
  show: { type: Boolean, required: true, default: false },
  account: { type: Object, required: true, default: null },
  user_id: { type: String, required: true, default: null },
});

// Emits
const emit = defineEmits(["closed", "success", "error", "aierror", "aisuccess", "requested", 'silent_success']);

// Stores
const userStore = useUserStore();
const partiesStore = usePartiesStore();
const accountsStore = useAccountsStore();
const partnerStore = usePartnerStore();
const emailsStore = useEmailsStore();

// Globals
const open = ref(false);
const updatingAccreditedStatus = ref(false);
const runningKycAml = ref(false);
const runningAml = ref(false);
const requestingReview = ref(false);
const accreditationStatus = ref(props.account.accounts_protected.accredited_status);

// Mounted
onMounted(async () => {
  open.value = props.show;
});

// Confirm Action START
const show_cofirm = ref(false);
const confirm_action_type = ref(null);
const confirm_title = computed(() => {
  if (confirm_action_type.value === "run_kyc_aml") return "Run KYC & AML";
  if (confirm_action_type.value === "run_aml") return "Run AML";
  if (confirm_action_type.value === "request_review") return "Request Opera Manual Review";
  return "Confirm Action";
});
const confirm_message = computed(() => {
  if (confirm_action_type.value === "run_kyc_aml") return "Are you sure you want to run KYC & AML? As a reminder each KYC & AML run costs $0.75.";
  if (confirm_action_type.value === "run_aml") return "Are you sure you want to run AML? As a reminder each AML run costs $0.75.";
  if (confirm_action_type.value === "request_review")
    return "Are you sure you want to request a manual review? Each manual review costs $25 for individuals and $75 for entities. A manual review can take up to 3 business days to complete. Our average response time is 1 business day.";
  return "Are you sure you want to perform this action?";
});
function confirmAction(type) {
  confirm_action_type.value = type;
  show_cofirm.value = true;
}
function actionConfirmed() {
  show_cofirm.value = false;
  if (confirm_action_type.value === "run_kyc_aml") runKycAml(props.account);
  if (confirm_action_type.value === "run_aml") runAml(props.account);
  if (confirm_action_type.value === "request_review") requestManualReview(props.account, partnerStore.partner.title);
  confirm_action_type.value = null;
}
// Confirm Action END

// Update Accreditation Status START
const reason_for_accreditation = ref("Income");
const notes = ref("Updated in Opera");
const reviewer = ref("");
async function updateAccreditationStatus(account) {
  updatingAccreditedStatus.value = true;
  if (!userStore.user.party_id) {
    emit("aierror");
    return;
  }
  reviewer.value = `${userStore.user.parties.first_name} ${userStore.user.parties.last_name}`;
  let formatted_rfa = reason_for_accreditation.value;
  if (reason_for_accreditation.value === "All Parties Accredited") {
    formatted_rfa = "All parties accredited";
  }
  const successful = await accountsStore.updateAccreditationStatus(
    account,
    accreditationStatus.value,
    formatted_rfa,
    notes.value,
    reviewer.value,
  );
  if (!successful) {
    emit("error");
    return;
  }
  updatingAccreditedStatus.value = false;
  open.value = false;
  emit("aisuccess");
}
// Update Accreditation Status END


async function runKycAml(a) {
  // a is the account
  runningKycAml.value = true;
  // Run KYC AML for primary party
  const successful = await partiesStore.performKycAml(a.parties.id, a.parties.tapi_party_id);
  if (!successful) {
    runningKycAml.value = false;
    emit("error");
    return;
  }

  // Run KYC AML for secondary party
  if (a.secondary_party) {
    const successful = await partiesStore.performKycAml(a.secondary_party.id, a.secondary_party.tapi_party_id);
    if (!successful) {
      runningKycAml.value = false;
      emit("error");
      return;
    }
  }

  runningKycAml.value = false;
  open.value = false;
  emit("success");
}

async function runAml(a) {
  // a is the account
  runningAml.value = true;
  // Run KYC AML for primary party
  const successful = await partiesStore.performAml(a.parties.id, a.parties.tapi_party_id);
  if (!successful) {
    runningKycAml.value = false;
    emit("error");
    return;
  }

  // Run KYC AML for secondary party
  if (a.secondary_party) {
    const successful = await partiesStore.performAml(a.secondary_party.id, a.secondary_party.tapi_party_id);
    if (!successful) {
      runningKycAml.value = false;
      emit("error");
      return;
    }
  }

  runningAml.value = false;
  open.value = false;
  emit("success");
}

async function requestManualReview(a, partner_title) {
  // a is the account
  requestingReview.value = true;
  const successful = await emailsStore.requestManualReview(a.parties, partner_title);
  if (!successful) {
    requestingReview.value = false;
    emit("error");
    return;
  }

  if (partnerStore.partner.partners_protected.managed_kyc_aml) {
    const success = partiesStore.requestManualKycAmlReview(a.parties.id, a.parties.tapi_party_id);
    if (!success) {
      requestingReview.value = false;
      emit("error");
      return;
    }
  }

  if (a.secondary_party) {
    const successful = await emailsStore.requestManualReview(a.secondary_party, partner_title);
    if (!successful) {
      requestingReview.value = false;
      emit("error");
      return;
    }

    if (partnerStore.partner.partners_protected.managed_kyc_aml) {
      const success = partiesStore.requestManualKycAmlReview(a.secondary_party.id, a.secondary_party.tapi_party_id);
      if (!success) {
        requestingReview.value = false;
        emit("error");
        return;
      }
    }
  }

  requestingReview.value = false;
  open.value = false;
  emit("requested");
}

// Watchers
watch(open, async (value) => {
  if (!value) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    emit("closed");
  }
});
</script>

<style scoped>
.construction-bg {
  background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23fde047' fill-opacity='0.3' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E");
}
</style>
